import React, { useState } from "react";

import SendFeedbackButton from "./SendFeedbackButton";
import Select from "../Select";
import StorySheet from "./StorySheet";
import { CLOZE_MODE, SMART_MODE } from "./storiesEvents";

const MODES = [
  { label: "Reading", value: SMART_MODE },
  { label: "Fill in the blanks", value: CLOZE_MODE }
];

function ResetStoryButton({ onReset }) {
  const [isConfirming, setIsConfirming] = useState(false);

  if (!isConfirming) {
    return <button className="btn btn-default btn-lg joystix btn-block" type="button" onClick={() => setIsConfirming(true)}>Reset story</button>;
  }

  return (
    <div className="story-reset-confirm">
      <p>Reset this story? Your answers and completed state will be cleared.</p>
      <div className="story-sheet-buttons">
        <button className="btn btn-default btn-lg joystix" type="button" onClick={() => setIsConfirming(false)}>Cancel</button>
        <button className="btn btn-danger btn-lg joystix story-sheet-primary" type="button" onClick={onReset}>Reset</button>
      </div>
    </div>
  );
}

function DeleteClozeTextButton({ onDelete }) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const deleteClozeText = async () => {
    setIsDeleting(true);
    setHasError(false);
    try {
      await onDelete();
    } catch {
      setIsDeleting(false);
      setHasError(true);
    }
  };

  if (!isConfirming) {
    return <button className="btn btn-default btn-lg joystix btn-block" data-testid="story-delete" type="button" onClick={() => setIsConfirming(true)}>Delete cloze-text</button>;
  }

  return (
    <div className="story-reset-confirm">
      <p>Delete this cloze-text? This can't be undone.</p>
      {hasError && <p className="text-danger" data-testid="story-delete-error">Couldn't delete it. Please try again.</p>}
      <div className="story-sheet-buttons">
        <button className="btn btn-default btn-lg joystix" disabled={isDeleting} type="button" onClick={() => setIsConfirming(false)}>Cancel</button>
        <button className="btn btn-danger btn-lg joystix story-sheet-primary" data-testid="story-delete-confirm" disabled={isDeleting} type="button" onClick={deleteClozeText}>
          {isDeleting ? <span className="fa fa-circle-o-notch fa-spin" data-testid="story-delete-loading" /> : "Delete"}
        </button>
      </div>
    </div>
  );
}

export default function StorySettingsSheet({ audio, isCompleted, mode, showAudioPlayer, onClose, onDelete, onMarkCompleted, onModeChange, onReset, onSendFeedback, onShowAudioPlayerChange }) {
  return (
    <StorySheet testId="story-settings-sheet" title="Settings" onClose={onClose}>
      <label className="story-setting">
        <span className="story-setting-title">Learning mode</span>
        <Select className="story-setting-select" data-testid="story-mode-select" value={mode} onChange={(event) => onModeChange(event.target.value)}>
          {MODES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Select>
      </label>
      {audio.isAvailable && (
        <>
          <label className="story-setting story-setting-switch">
            <span className="story-setting-title">Show audio player</span>
            <input
              checked={showAudioPlayer}
              className="story-switch"
              data-testid="story-show-audio-player"
              role="switch"
              type="checkbox"
              onChange={(event) => onShowAudioPlayerChange(event.target.checked)}
            />
          </label>
          {audio.voices.length > 0 && (
            <label className="story-setting">
              <span className="story-setting-title">Voice</span>
              <Select
                className="story-setting-select"
                data-testid="story-voice-select"
                value={audio.voiceId ?? ""}
                onChange={(event) => audio.changeVoice(event.target.value || null)}
              >
                <option value="">System default</option>
                {audio.voices.map((voice) => <option key={voice.voiceURI} value={voice.voiceURI}>{voice.name}</option>)}
              </Select>
            </label>
          )}
          <label className="story-setting">
            <span className="story-setting-header">
              <span className="story-setting-title">Speed</span>
              <span className="story-setting-value">{audio.speed}x</span>
            </span>
            <input
              aria-label="Speed"
              className="story-speed-slider"
              max="2"
              min="0.5"
              step="0.1"
              type="range"
              value={audio.speed}
              onChange={(event) => audio.changeSpeed(Math.round(Number(event.target.value) * 100) / 100)}
            />
          </label>
        </>
      )}
      <div className="story-sheet-buttons-stack">
        {!isCompleted && !!onMarkCompleted && (
          <button className="btn btn-success btn-lg joystix btn-block" type="button" onClick={onMarkCompleted}>Mark as completed</button>
        )}
        {!!onReset && <ResetStoryButton onReset={onReset} />}
        {!!onDelete && <DeleteClozeTextButton onDelete={onDelete} />}
        <SendFeedbackButton onClick={onSendFeedback} />
      </div>
    </StorySheet>
  );
}
